import * as Yup from 'yup';
import {
  PlanWizardFormValues,
  WizardFixedCostValues,
  WizardProjectedRevenueValues
} from '../types';

export const MAX_NOTES = 1000;

// FR-088 (widened to the `_PerAc` fields per 2026-08-26 design QA): every
// field defaults to `null`, not `0`, so a fresh create-mode wizard renders
// them blank rather than "$0.00".
export const initialFixedCostValues: WizardFixedCostValues = {
  landRent: null,
  landRent_PerAc: null,
  equipment: null,
  equipment_PerAc: null,
  insurance: null,
  insurance_PerAc: null,
  labor: null,
  labor_PerAc: null,
  repairs: null,
  repairs_PerAc: null,
  taxes: null,
  taxes_PerAc: null,
  returnToMgt: null,
  returnToMgt_PerAc: null
};

// Same blank-default treatment for the four editable *amount* fields.
// `cropSharePercent` starts at `100` like legacy Plans; it may go blank while
// editing, but blur restores `100`.
export const initialProjectedRevenueValues: WizardProjectedRevenueValues = {
  yieldPerArea: null,
  yieldTotal: 0,
  pricePerUnit: null,
  fsaPayment: null,
  fsaPaymentPerAc: null,
  cropSharePercent: 100
};

export const initialPlanValues: PlanWizardFormValues = {
  name: '',
  cropId: '',
  estimatedAreaValue: 1,
  estimatedAreaUnit: 'acre',
  area: 1,
  fixedCost: initialFixedCostValues,
  projectedRevenue: initialProjectedRevenueValues,
  notes: '',
  tagIds: [],
  cropZones: [],
  totalAppliedAcreage: 0,
  planProductEntries: [],
  isEditOrDuplicateMode: false
};

export const initialDetailsValues = {
  name: initialPlanValues.name,
  cropId: initialPlanValues.cropId,
  estimatedAreaValue: initialPlanValues.estimatedAreaValue,
  estimatedAreaUnit: initialPlanValues.estimatedAreaUnit,
  area: initialPlanValues.area,
  fixedCost: initialPlanValues.fixedCost,
  projectedRevenue: initialPlanValues.projectedRevenue,
  notes: initialPlanValues.notes,
  tagIds: initialPlanValues.tagIds
};

export const initialPropertiesValues = {
  cropZones: initialPlanValues.cropZones,
  totalAppliedAcreage: initialPlanValues.totalAppliedAcreage
};

export const initialProductsValues = {
  planProductEntries: initialPlanValues.planProductEntries
};

/**
 * Reuses `Plan/schema.js`'s exact validation rules (name required/max 128,
 * area required) — FR-005/FR-015. `Plan/schema.js`'s own `revenue`/`crops`
 * keys are a pre-existing, unrelated legacy artifact (the real projected-
 * revenue fields are `projectedRevenue.*`, not validated today either) — not
 * reproduced here since porting fields "as-is" means matching today's actual
 * validated behavior, not inventing new coverage.
 */
export const planWizardValidationSchema = Yup.object().shape({
  name: Yup.string()
    .label('Plan ID')
    .trim()
    .max(128, 'Name must be 128 characters or less.')
    .required('Required')
    .typeError('Required'),
  area: Yup.number()
    .required('Required')
    .typeError('Required'),
  notes: Yup.string().max(
    MAX_NOTES,
    `Notes cannot exceed ${MAX_NOTES.toLocaleString()} characters`
  )
});

export default {
  initialValues: initialPlanValues,
  validationSchema: planWizardValidationSchema
};
